import type { ApiClient } from '../client';
import type { PoolOrder, PurchaseResult } from './types';

/**
 * Full pool_order payload returned by `GET /pool_orders/:id` on the iframe
 * namespace. Embeds the offer (with videos) and items (with opened_at /
 * badge / fair_market_value) — everything the reveal screen needs in one
 * round-trip.
 */
export interface IframePoolOrder {
  id: string;
  status: string;
  quantity: number;
  totalPrice: number;
  createdAt: string | null;
  poolOffer: {
    id: number;
    title: string;
    price: string;
    packImageUrl: string | null;
    /** Per-operator pedestal/plinth the winner card stands on. Null when
     *  the operator hasn't uploaded one — WinnerStep falls back to the
     *  bundled `default-piedestal.png`. */
    pedestalImageUrl: string | null;
    packVideo: string | null;
    openPackVideo: string | null;
    webPackVideo: string | null;
    webOpenPackVideo: string | null;
  };
  poolItems: IframePoolOrderItem[];
}

export interface IframePoolOrderItem {
  id: string;
  title: string;
  imageUrl: string | null;
  itemPrice: number;
  openedAt: string | null;
  fairMarketValue: number | null;
  /** Backend pool_item status — `on_hands` means the user owns it
   *  physically and a shipping action is allowed. Other values
   *  (`sold`, `shipped`, etc.) gate that CTA out. */
  status: string | null;
  /** Badge — rarity tier + the extra reveal-animation fields. `grade`
   *  is the grading code shown on the slab (e.g. "PSA 10"); `character`
   *  is the card subject (e.g. "Robert Jopin"). Both are part of the
   *  badge envelope because they only matter on a tiered pull. Filled
   *  in by the dev-mock for now; backend will populate later. */
  badge: {
    id:        number | null;
    name:      string | null;
    grade:     string | null;
    character: string | null;
  } | null;
  bestActiveOffer: {
    id: number;
    grossPrice: number;
    netPrice: number;
    buybackRate: number | null;
    /** ISO timestamp — when the offer auto-fires for the SHIPPING
     *  decision. Drives the countdown header on the Create Shipping
     *  modal. Maps to `best_active_offer.auto_sale_at`. */
    autoSaleAt: string | null;
    /** ISO timestamp — when the offer auto-sells (buyback) if the
     *  user takes no action. Drives the pie-progress chip on the My
     *  Packs cards. Maps to `best_active_offer.auto_sell_at`. */
    autoSellAt: string | null;
    /** ISO timestamp — when the offer was issued. Pairs with
     *  `autoSellAt` to compute the real pie-progress denominator
     *  (`autoSellAt - createdAt`) in CountdownBadge. */
    createdAt: string | null;
  } | null;
  /** Pool order this item belongs to — required for the sell action
   *  (POST /pool_orders/:order_id/sell). */
  poolOrderId: number | null;
  /** Source pack — feeds the "Buy Another Repack" CTA in the card modal. */
  soldFromPoolOffer: {
    id: number;
    title: string;
    imageUrl: string | null;
  } | null;
  /** Shipping cost breakdown — populated by the dev mock until the
   *  backend ships `delivery_info` on the pool_item. Range fields
   *  (shipping fee / total) come as min/max because the real number
   *  depends on the carrier quote, which is only resolved post-confirm. */
  deliveryInfo: {
    shippingFeeMin: number;
    shippingFeeMax: number;
    salesTax: number;
    handlingFee: number;
    totalMin: number;
    totalMax: number;
  } | null;
}

interface RawOfferEmbed {
  id?: number | string;
  title?: string;
  price?: number | string | null;
  pack_image_url?: string | null;
  pedestal_image_url?: string | null;
  pack_video?: string | null;
  open_pack_video?: string | null;
  web_pack_video?: string | null;
  web_open_pack_video?: string | null;
}

export interface RawPoolItemEmbed {
  id?: number | string;
  title?: string;
  image_url?: string | null;
  item_price?: number | string | null;
  opened_at?: string | null;
  fair_market_value?: number | string | null;
  status?: string | null;
  badge?: {
    id?: number | null;
    name?: string | null;
    grade?: string | null;
    character?: string | null;
  } | null;
  best_active_offer?: {
    id?: number;
    gross_price?: number | string;
    net_price?: number | string;
    buyback_rate?: number | string | null;
    auto_sale_at?: string | null;
    auto_sell_at?: string | null;
    created_at?: string | null;
  } | null;
  pool_order_id?: number | string | null;
  sold_from_pool_offer?: {
    id?: number | string;
    title?: string | null;
    image_url?: string | null;
  } | null;
  delivery_info?: {
    shipping_fee_min?: number | string | null;
    shipping_fee_max?: number | string | null;
    sales_tax?: number | string | null;
    handling_fee?: number | string | null;
    total_min?: number | string | null;
    total_max?: number | string | null;
  } | null;
}

interface RawPoolOrder {
  id?: number | string;
  status?: string;
  quantity?: number | string;
  total_price?: number | string | null;
  created_at?: string | null;
  pool_offer?: RawOfferEmbed;
  pool_items?: RawPoolItemEmbed[];
}

function mapOfferEmbed(o: RawOfferEmbed | undefined): IframePoolOrder['poolOffer'] {
  return {
    id: Number(o?.id ?? 0),
    title: o?.title ?? '',
    price: o?.price !== undefined && o?.price !== null ? String(o.price) : '0',
    packImageUrl: (o?.pack_image_url as string) || null,
    pedestalImageUrl: (o?.pedestal_image_url as string) || null,
    packVideo: (o?.pack_video as string) || null,
    openPackVideo: (o?.open_pack_video as string) || null,
    webPackVideo: (o?.web_pack_video as string) || null,
    webOpenPackVideo: (o?.web_open_pack_video as string) || null,
  };
}

export function mapPoolItemEmbed(i: RawPoolItemEmbed): IframePoolOrderItem {
  const badge =
    i.badge &&
    (i.badge.id !== undefined ||
      i.badge.name !== undefined ||
      i.badge.grade !== undefined ||
      i.badge.character !== undefined)
      ? {
          id:        i.badge.id ?? null,
          name:      i.badge.name ?? null,
          grade:     i.badge.grade ?? null,
          character: i.badge.character ?? null,
        }
      : null;

  const o = i.best_active_offer;
  const bestActiveOffer = o && o.id !== undefined
    ? {
        id: Number(o.id),
        grossPrice: Number(o.gross_price ?? 0),
        netPrice: Number(o.net_price ?? 0),
        buybackRate:
          o.buyback_rate !== undefined && o.buyback_rate !== null
            ? Number(o.buyback_rate)
            : null,
        autoSaleAt:
          typeof o.auto_sale_at === 'string' && o.auto_sale_at.length > 0
            ? o.auto_sale_at
            : null,
        autoSellAt:
          typeof o.auto_sell_at === 'string' && o.auto_sell_at.length > 0
            ? o.auto_sell_at
            : null,
        createdAt:
          typeof o.created_at === 'string' && o.created_at.length > 0
            ? o.created_at
            : null,
      }
    : null;

  const d = i.delivery_info;
  const deliveryInfo =
    d &&
    (d.shipping_fee_min != null ||
      d.shipping_fee_max != null ||
      d.sales_tax != null ||
      d.handling_fee != null ||
      d.total_min != null ||
      d.total_max != null)
      ? {
          shippingFeeMin: Number(d.shipping_fee_min ?? 0),
          shippingFeeMax: Number(d.shipping_fee_max ?? 0),
          salesTax: Number(d.sales_tax ?? 0),
          handlingFee: Number(d.handling_fee ?? 0),
          totalMin: Number(d.total_min ?? 0),
          totalMax: Number(d.total_max ?? 0),
        }
      : null;

  const offer = i.sold_from_pool_offer;
  const soldFromPoolOffer =
    offer && (typeof offer.id === 'number' || typeof offer.id === 'string')
      ? {
          id: Number(offer.id),
          title: (offer.title ?? '').toString(),
          imageUrl:
            typeof offer.image_url === 'string' && offer.image_url
              ? offer.image_url
              : null,
        }
      : null;

  return {
    id: String(i.id ?? ''),
    title: i.title ?? '',
    imageUrl: (i.image_url as string) || null,
    itemPrice: Number(i.item_price ?? 0),
    openedAt: i.opened_at ?? null,
    fairMarketValue:
      i.fair_market_value !== undefined && i.fair_market_value !== null
        ? Number(i.fair_market_value)
        : null,
    status: typeof i.status === 'string' && i.status.length > 0 ? i.status : null,
    // Badge now carries `grade` + `character` alongside id/name (see
    // type). Backend currently sends only id/name; PackReveal's
    // dev-mock fills in the extra fields when MOCK_REVEAL_BADGE is on.
    badge,
    bestActiveOffer,
    poolOrderId:
      i.pool_order_id !== undefined && i.pool_order_id !== null
        ? Number(i.pool_order_id)
        : null,
    soldFromPoolOffer,
    deliveryInfo,
  };
}

/**
 * Body shape for {@link sellPoolItems}. Either we sell every item the
 * backend's items_scope yields (`{ all: true }`) or we pass an explicit
 * list of pool_item ids. Discriminated so callers can't mix both.
 */
export type SellPoolItemsParams =
  | { all: true }
  | { poolItemIds: (string | number)[] };

/**
 * Sells a single pool_item back to the pool for its `best_active_offer`
 * gross price — GamePlatform `Sell::Run` credits the player's wallet via
 * seamless wallet. No Solana round-trip in the iframe path.
 */
export async function sellPoolItem(
  client: ApiClient,
  orderId: string | number,
  poolItemId: string | number,
): Promise<void> {
  await sellPoolItems(client, orderId, { poolItemIds: [poolItemId] });
}

/**
 * Bulk variant of {@link sellPoolItem}. Hits `/pool_orders/:order_id/sell`
 * with either `all: true` (server picks every item in items_scope) or an
 * explicit `pool_item_ids` list. The `:order_id` is now just a route
 * anchor — the backend scopes via items_scope, not via this id — so
 * callers can pass any pool_order they own.
 */
export async function sellPoolItems(
  client: ApiClient,
  orderId: string | number,
  params: SellPoolItemsParams,
): Promise<void> {
  const body =
    'all' in params
      ? { all: true }
      : { pool_item_ids: params.poolItemIds };
  await client.request(
    `/loot_machine/api/game_platform/iframe/v1/pool_orders/${encodeURIComponent(String(orderId))}/sell`,
    {
      method: 'POST',
      body,
    },
  );
}

export async function getPoolOrder(
  client: ApiClient,
  orderId: string | number,
): Promise<IframePoolOrder> {
  const raw = await client.request<{ pool_order: RawPoolOrder }>(
    `/loot_machine/api/game_platform/iframe/v1/pool_orders/${encodeURIComponent(String(orderId))}`,
    { method: 'GET' },
  );
  const o = raw.pool_order ?? ({} as RawPoolOrder);
  return {
    id: String(o.id ?? ''),
    status: o.status ?? 'pending',
    quantity: Number(o.quantity ?? 1),
    totalPrice: Number(o.total_price ?? 0),
    createdAt: o.created_at ?? null,
    poolOffer: mapOfferEmbed(o.pool_offer),
    poolItems: (o.pool_items ?? []).map(mapPoolItemEmbed),
  };
}

// Iframe-specific endpoints on backend wrap payload in `{ pool_order: {...} }`
// (see Api::GamePlatform::Iframe::V1::PoolOrdersController). The serialized
// pool_order uses AMS attributes-adapter so it's flat: id/status/quantity/
// pool_offer/pool_items/... — accessed via `raw.pool_order`.

interface RawOrderResult {
  id: number | string;
  status: string;
  quantity: number;
  pool_offer?: { price?: string | number };
  pool_items?: Array<{
    id: number | string;
    title?: string;
    name?: string;
    item_price?: string | number;
    images?: Array<{ url: string }> | { url: string };
    badge?: { name?: string } | null;
  }>;
}

function dollarsToCents(v: string | number | undefined | null): number {
  if (v === undefined || v === null) return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

type RawImages = Array<{ url: string }> | { url: string } | undefined;

function firstImageUrl(images: RawImages): string {
  if (!images) return '';
  if (Array.isArray(images)) return images[0]?.url ?? '';
  return images.url ?? '';
}

function adaptOrder(r: RawOrderResult): PoolOrder {
  return {
    id: String(r.id),
    price_cents: dollarsToCents(r.pool_offer?.price),
    state: (r.status as PoolOrder['state']) ?? 'pending',
  };
}

export async function createPoolOrder(
  client: ApiClient,
  poolOfferId: string,
  quantity: number = 1,
): Promise<PoolOrder> {
  const safeQuantity = Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 1;
  const raw = await client.request<{ pool_order: RawOrderResult }>(
    `/loot_machine/api/game_platform/iframe/v1/pool_offers/${encodeURIComponent(poolOfferId)}/pool_orders`,
    { method: 'POST', body: { pool_order: { quantity: safeQuantity } } },
  );
  return adaptOrder(raw.pool_order);
}

export async function purchasePoolOrder(
  client: ApiClient,
  orderId: string,
): Promise<PurchaseResult> {
  const raw = await client.request<{ pool_order: RawOrderResult }>(
    `/loot_machine/api/game_platform/iframe/v1/pool_orders/${encodeURIComponent(orderId)}/purchase`,
    { method: 'POST' },
  );
  const r = raw.pool_order;
  return {
    order: adaptOrder(r),
    items: (r.pool_items ?? []).map((i) => ({
      id: String(i.id),
      name: i.title ?? i.name ?? '',
      image_url: firstImageUrl(i.images),
      rarity: i.badge?.name ?? 'common',
      value_cents: dollarsToCents(i.item_price),
    })),
  };
}
